/**
 * `/library` — the workspace-wide Library index (Sprint 23.7).
 *
 * One surface for every library node a workforce produces: pages, memories,
 * files, and prompts. Filter chips (All / Pages / Memories / Files / Prompts)
 * switch between a stacked all-view and a single-panel focused view. Reuses the
 * existing cockpit panels verbatim — Pages workspace-wide, the rest scoped to
 * the workspace root (mirroring the root cockpit's composition).
 *
 * The `?filter=` query param is deep-linkable, so the deprecated `/prompts`
 * route redirects here as `/library?filter=prompts`.
 */

import { useMemo, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { Card, EmptyState } from "../components/primitives";
import { PagesPanel } from "../components/cockpit/PagesPanel";
import { MemoriesPanel } from "../components/cockpit/MemoriesPanel";
import { FilesPanel } from "../components/cockpit/FilesPanel";
import { PromptsPanel } from "../components/cockpit/PromptsPanel";
import { useChatScope } from "../hooks/use-chat-scope";
import { useChildNodes } from "../hooks/use-child-nodes";
import { useListPromptsInScope } from "../hooks/use-list-prompts-in-scope";
import { useWorkspaceInfo, useWorkspacePages } from "../../lib/hooks/use-kuvio";

type LibraryFilter = "all" | "pages" | "memories" | "files" | "prompts";

const FILTERS: LibraryFilter[] = ["all", "pages", "memories", "files", "prompts"];

function normaliseFilter(raw: string | null): LibraryFilter {
  return FILTERS.includes(raw as LibraryFilter) ? (raw as LibraryFilter) : "all";
}

export function LibraryPage() {
  const workspace = useWorkspaceInfo();
  const rootNodeId = workspace.data?.root_node_id as string | undefined;
  const workspaceName = workspace.data?.workspace?.name ?? "Workspace";

  const [searchParams, setSearchParams] = useSearchParams();
  const filter = normaliseFilter(searchParams.get("filter"));

  useChatScope({ kind: "workspace", label: "Library" }, null, []);

  // Counts for the filter chips. Pages are workspace-wide; the rest mirror the
  // root cockpit's direct-children scoping.
  const pages = useWorkspacePages(true);
  const memories = useChildNodes(rootNodeId, "memory");
  const files = useChildNodes(rootNodeId, "file");
  const prompts = useListPromptsInScope(rootNodeId);

  const counts = useMemo(
    () => ({
      pages: pages.data?.length ?? 0,
      memories: memories.data?.length ?? 0,
      files: files.data?.length ?? 0,
      prompts: prompts.data?.length ?? 0,
    }),
    [pages.data, memories.data, files.data, prompts.data],
  );
  const total = counts.pages + counts.memories + counts.files + counts.prompts;

  const anyLoading = pages.isLoading || memories.isLoading || files.isLoading || prompts.isLoading;
  const isEmpty = !anyLoading && total === 0;

  const setFilter = (f: LibraryFilter) => {
    const next = new URLSearchParams(searchParams);
    if (f === "all") next.delete("filter");
    else next.set("filter", f);
    setSearchParams(next);
  };

  const chipLabel: Record<LibraryFilter, string> = {
    all: "All",
    pages: "Pages",
    memories: "Memories",
    files: "Files",
    prompts: "Prompts",
  };
  const chipCount: Record<LibraryFilter, number | undefined> = {
    all: total || undefined,
    pages: counts.pages || undefined,
    memories: counts.memories || undefined,
    files: counts.files || undefined,
    prompts: counts.prompts || undefined,
  };

  const showPages = filter === "all" || filter === "pages";
  const showMemories = filter === "all" || filter === "memories";
  const showFiles = filter === "all" || filter === "files";
  const showPrompts = filter === "all" || filter === "prompts";

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Library</h1>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>
            Everything your workforce produces — pages, memories, files, and prompts.
          </div>
        </div>
      </div>

      <div style={chipRowStyle}>
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} style={chipStyle(filter === f)}>
            {chipLabel[f]}
            {chipCount[f] !== undefined && <span style={chipCountStyle}>{chipCount[f]}</span>}
          </button>
        ))}
      </div>

      {isEmpty ? (
        <Card>
          <EmptyState>
            Library is empty — pages, memories, and files will appear here as your workforce produces them.
          </EmptyState>
        </Card>
      ) : !rootNodeId ? (
        <Card>
          <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading library…</span>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {showPages && <PagesPanel nodeId={rootNodeId} workspaceWide />}
          {showMemories && <MemoriesPanel nodeId={rootNodeId} />}
          {showFiles && <FilesPanel nodeId={rootNodeId} nodeName={workspaceName} />}
          {showPrompts && <PromptsPanel nodeId={rootNodeId} nodeName={workspaceName} />}
        </div>
      )}
    </div>
  );
}

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: 14,
};

const chipRowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  marginBottom: 14,
};

function chipStyle(active: boolean): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: active ? 500 : 400,
    padding: "3px 9px",
    borderRadius: 999,
    border: `0.5px solid ${active ? "var(--color-border-tertiary)" : "transparent"}`,
    background: active ? "var(--color-background-secondary)" : "transparent",
    color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };
}

const chipCountStyle: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  fontVariantNumeric: "tabular-nums",
};
